import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import useAuth from '../../Hooks/useAuth';
import AxiosUtil from '../../Utils/AxiosUtil';
import {
    Alert as ServaAlert,
    Button,
    Col,
    Collapse,
    DateTimePicker,
    Divider,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
    formatDate,
    formatDateISO,
} from '@servahealth/serva-ui-kit';
import TextArea from 'antd/es/input/TextArea';
import AutomaticDeliveryAdvanced from './AutomaticDeliveryAdvanced';
import AutoRunModel from '../../Interfaces/Responses/Schedules/AutoRun';
import AutoRunSchedule from '../../Interfaces/Responses/Schedules/AutoRunSchedule';
import { AutoRunTarget, AutoRunTargetType } from '../../Interfaces/Responses/Schedules/AutoRunTarget';
import AutoRunTargetFTP from '../../Interfaces/Responses/Schedules/AutoRunTargetFTP';
import AutoRunTargetEmail from '../../Interfaces/Responses/Schedules/AutoRunTargetEmail';
import Report from '../../Interfaces/Responses/Dashboard/Report';
import Schedules from '../../Constants/Endpoints/Schedules';
import SetAutoRunDetailsByReportIDRequestModel from '../../Interfaces/Requests/Schedules/SetAutoRunDetailsByReportIDRequestModel';

type AutomaticDeliveryProps = {
    report: Report | undefined;
    close: () => void;
};

const AutomaticDelivery: React.FC<AutomaticDeliveryProps> = ({ report, close, }) => {
    const dateFormat = 'MM/DD/YYYY hh:mm A';

    const { user } = useAuth();
    const [formAutomaticDelivery] = Form.useForm();
    //const [autoRun, setAutoRun] = useState<AutoRun | null>();

    const [adDescription, setADDescription] = useState<string | undefined>();
    const [adNextRun, setADNextRun] = useState<string | undefined>();
    const [adDeliveryFormat, setADDeliveryFormat] = useState<number | undefined>();
    const [adFileName, setADFileName] = useState<string | undefined>();
    const [adRunFrequencyType, setADRunFrequencyType] = useState<number | undefined>();
    const [adRunFrequencyValue, setADRunFrequencyValue] = useState<number | undefined>();
    const [adEmailSubject, setADEmailSubject] = useState<string | undefined>();
    const [adEmailBody, setADEmailBody] = useState<string | undefined>();
    const [adParentAutoRunID, setADParentAutoRunID] = useState<number | undefined>();
    const [adReportInputID, setADReportInputID] = useState<number | undefined>();
    const [adReportInputs, setADReportInputs] = useState<string | undefined>();
    const [adPreRunSQL, setADPreRunSQL] = useState<string | undefined>();
    const [adMonitorQueueQuery, setADMonitorQueueQuery] = useState<string | undefined>();

    const [adAutoRunTargetEmail, setADTargetEmail] = useState<AutoRunTargetEmail | undefined>();
    const [adAutoRunTargetFTP, setADTargetFTP] = useState<AutoRunTargetFTP | undefined>();

    const [currentError, setCurrentError] = useState('');

    useEffect(() => {
        //setAutoRun(report?.autoRun);

        setADDescription(((report) && (report.id > 0)) ? report.autoRun.description : '');
        setADNextRun(formatDate(((report) && (report.id > 0)) ? report.autoRun.nextRun : new Date().toString(), dateFormat));
        setADDeliveryFormat(((report) && (report.id > 0)) ? report.autoRun.deliveryFormat : 0);
        setADFileName(((report) && (report.id > 0)) ? report.autoRun.fileName : '');
        setADRunFrequencyType(((report) && (report.id > 0)) ? report.autoRun.frequencyType : 0);
        setADRunFrequencyValue(((report) && (report.id > 0)) ? report.autoRun.frequencyValue : 1);
        setADEmailSubject(((report) && (report.id > 0)) ? report.autoRun.subject : '');
        setADEmailBody(((report) && (report.id > 0)) ? report.autoRun.body : '');
        setADParentAutoRunID(((report) && (report.id > 0)) ? report.autoRun.parentAutoRunID : 0);
        setADReportInputID(((report) && (report.id > 0)) ? report.autoRun.reportInputID : 0);
        setADReportInputs(((report) && (report.id > 0)) ? report.autoRun.reportInputs : '');
        setADPreRunSQL(((report) && (report.id > 0)) ? report.autoRun.preSQL : '');
        setADMonitorQueueQuery(((report) && (report.id > 0)) ? report.autoRun.monitorQueueQuery : '');

        setADTargetFTP(((report) && (report.id > 0)) ? report.autoRun.ftpTarget : undefined);
        setADTargetEmail(((report) && (report.id > 0)) ? report.autoRun.emailTarget : undefined);

        // ---- DEBUG
        // console.log(report?.autoRun);
        // ---- ENDDEBUG
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { mutate: setAutoRunDetails, isLoading: isSetAutoRunDetailsLoading, isError: isSetAutoRunDetailsError, error: setAutoRunDetailsError, } = useMutation(
        async (newAutoRun: AutoRunModel) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Schedules.SET_AUTORUN_DETAILS_BY_REPORT_ID}`;
                const requestData: SetAutoRunDetailsByReportIDRequestModel = {
                    currentToken: user?.runningUser.currentToken ?? '',
                    newAutoRunDetails: newAutoRun,
                };
                const responseData = await AxiosUtil.post<boolean, SetAutoRunDetailsByReportIDRequestModel>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('SET_AUTORUN_DETAILS_BY_REPORT_ID: [Set Autorun Details] Internal Error: ' + err);
                console.error('SET_AUTORUN_DETAILS_BY_REPORT_ID [Set Autorun Details] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any) => {
                setCurrentError('SET_AUTORUN_DETAILS_BY_REPORT_ID: [Set Autorun Details] Internal Error: ' + err);
                console.error('SET_AUTORUN_DETAILS_BY_REPORT_ID [Set Autorun Details] Internal Error: ', err);
                ServaModal.error({
                    title: 'Automatic Delivery',
                    content: `Automatic delivery for report ${report?.id ?? 0} was unable to be scheduled. Please contact application support.`,
                });
            },
            onSuccess: (response: boolean, context: any) => {
                ServaModal.success({
                    title: 'Automatic Delivery',
                    content: `Automatic delivery for report ${report?.id ?? 0} was scheduled successfully.`,
                });
                close();
            },
        }
    );

    const onFinish = (values: any) => {
        const newSchedule: AutoRunSchedule =
        {
            scheduled: new Date(values.adNextRun as string ?? ''),
            executed: undefined,
            oneOffRun: (values.adFrequencyType === 0),
            id: report?.autoRun.schedule.id ?? 0,
            autoRunID: report?.autoRun.id ?? 0,
        };
        // ---- DEBUG
        // console.log(newSchedule);
        // ---- ENDDEBUG

        const newTargetFTP: AutoRunTargetFTP =
        {
            userID: adAutoRunTargetFTP?.userID ?? 0,
            ftpServerID: adAutoRunTargetFTP?.ftpServerID ?? 0,
            server: '',
            port: 0,
            userName: '',
            password: '',
            isSFTP: false,
            format: 0,
            delimiter: '',
            fixedWidthLayout: '',
            includeColumnHeaders: false,
            remoteFolder: '',
            fileNameQuery: '',
            pgp_Key_Name: '',
            id: adAutoRunTargetFTP?.id ?? 0,
            autoRunID: report?.autoRun.id ?? 0,
            autoRunScheduleID: report?.autoRun.schedule.id ?? 0,
            autoRunTargetType: AutoRunTargetType.FTP as number,
        };
        // ---- DEBUG
        // console.log(newTargetFTP);
        // ---- ENDDEBUG


        const newTargetEmail: AutoRunTargetEmail =
        {
            userID: adAutoRunTargetEmail?.userID ?? 0,
            targetEmail: adAutoRunTargetEmail?.targetEmail ?? '',
            fileFormat: Number(adAutoRunTargetEmail?.fileFormat as number ?? 0),
            id: adAutoRunTargetEmail?.id ?? 0,
            autoRunID: report?.autoRun.id ?? 0,
            autoRunScheduleID: report?.autoRun.schedule.id ?? 0,
            autoRunTargetType: AutoRunTargetType.Email as number,
        };
        // ---- DEBUG
        // console.log(newTargetEmail);
        // ---- ENDDEBUG

        const newAutoRun: AutoRunModel = {
            id: (report?.autoRun.id ?? 0) as number,
            description: values.adDescription as string,
            frequencyType: Number(values.adRunFrequencyType as number),
            frequencyValue: Number(values.adRunFrequencyValue as number),
            deliveryFormat: Number(values.adDeliveryFormat as number),
            combineOutput: true,
            reportID: (report?.id ?? 0) as number,
            reportName: (report?.name ?? '') as string,
            targets: new Array<AutoRunTarget>(),                    // to build
            fileName: values.adFileName as string,
            schedule: newSchedule,                                  // to test
            nextRunJS: '' as string,
            nextRun: formatDateISO(values.adNextRun as string),
            subject: values.adEmailSubject as string,
            body: values.adEmailBody as string,
            parentAutoRunID: values.adParentAutoRunID as number,
            preSQL: values.adPreRunSQL as string,
            reportInputs: values.adReportInputs as string,
            ftpTarget: newTargetFTP,                                // to test
            emailTarget: newTargetEmail,                            // to test
            childRuns: new Array<AutoRunModel>(),
            isQueued: (values.adMonitorQueueQuery as boolean) ? true : false,
            monitorQueueQuery: values.adMonitorQueueQuery as string,
            reportInputID: values.adReportInputID as number,
        };
        // ---- DEBUG
        // console.log(newAutoRun);
        // ---- ENDDEBUG

        setAutoRunDetails(newAutoRun);
    };

    const validateARDetails = (rule: any, value: string) => {
        if (rule.field === 'adDescription') {
            const newADDescription = formAutomaticDelivery.getFieldValue('adDescription');
            return (!newADDescription || newADDescription.length === 0) ? Promise.reject() : Promise.resolve();
        }
        else if (rule.field === 'adFileName') {
            const newADFileName = formAutomaticDelivery.getFieldValue('adFileName');
            return (!newADFileName || newADFileName.length === 0) ? Promise.reject() : Promise.resolve();
        }
        else if (rule.field === 'adRunFrequencyValue') {
            const newADFrequencyValue = formAutomaticDelivery.getFieldValue('adRunFrequencyValue');
            return (!newADFrequencyValue || newADFrequencyValue.length === 0) ? Promise.reject() : Promise.resolve();
        }
        else if (rule.field === 'adEmailSubject') { 
            const newADEmailSubject = formAutomaticDelivery.getFieldValue('adEmailSubject');
            return (!newADEmailSubject || newADEmailSubject.length === 0) ? Promise.reject() : Promise.resolve();
        }
        else if (rule.field === 'adEmailBody') { 
            const newADEmailBody = formAutomaticDelivery.getFieldValue('adEmailBody');
            return (!newADEmailBody || newADEmailBody.length === 0) ? Promise.reject() : Promise.resolve();
        }
        else {
            return Promise.reject();
        }
    };

    const handleADDescriptionUpdate = () => {
        let newADDescription = formAutomaticDelivery.getFieldValue('adDescription');

        setADDescription(newADDescription);
    };

    const handleADNextRunUpdate = (e: string) => {
        setADNextRun(e);
    };

    const handleADDeliveryFormatUpdate = (e: string) => {
        setADDeliveryFormat(parseInt(e));
    };

    const handleADFileNameUpdate = () => {
        let newADFileName = formAutomaticDelivery.getFieldValue('adFileName');

        setADFileName(newADFileName);
    };

    const handleADRunFrequencyTypeUpdate = (e: string) => {
        setADRunFrequencyType(parseInt(e));
    };

    const handleADRunFrequencyValueUpdate = () => {
        let newADRunFrequencyValue = formAutomaticDelivery.getFieldValue('adRunFrequencyValue');

        setADRunFrequencyValue(newADRunFrequencyValue);
    };

    const handleADEmailSubjectUpdate = () => {
        let newADEmailSubject = formAutomaticDelivery.getFieldValue('adEmailSubject');

        setADEmailSubject(newADEmailSubject);
    };

    const handleADEmailBodyUpdate = () => {
        let newADEmailBody = formAutomaticDelivery.getFieldValue('adEmailBody');

        setADEmailSubject(newADEmailBody);
    };

    const handleADParentAutoRunIDUpdate = () => {
        let newADParentAutoRunID = formAutomaticDelivery.getFieldValue('adParentAutoRunID');

        setADParentAutoRunID(newADParentAutoRunID);
    };

    const handleADReportInputIDUpdate = () => {
        let newADReportInputID = formAutomaticDelivery.getFieldValue('adReportInputID');

        setADReportInputID(newADReportInputID);
    };

    const handleADReportInputsUpdate = () => {
        let newADReportInputs = formAutomaticDelivery.getFieldValue('adReportInputs');

        setADReportInputs(newADReportInputs);
    };

    const handleADPreRunSQLUpdate = () => {
        let newADPreRunSQL = formAutomaticDelivery.getFieldValue('adPreRunSQL');

        setADPreRunSQL(newADPreRunSQL);
    };

    const handleADMonitorQueueQueryUpdate = () => {
        let newADMonitorQueueQuery = formAutomaticDelivery.getFieldValue('adMonitorQueueQuery');

        setADMonitorQueueQuery(newADMonitorQueueQuery);
    };

    const handleADAutoRunTargetEmailUpdate = (autoRunTargetEmail: AutoRunTargetEmail) => {

        setADTargetEmail(autoRunTargetEmail);
    };

    const handleADAutoRunTargetFTPUpdate = (autoRunTargetFTP: AutoRunTargetFTP) => {

        setADTargetFTP(autoRunTargetFTP);
    };

    const determineWhichError = () => {
        if ( isSetAutoRunDetailsError ) {
            return currentError + ' ' + (setAutoRunDetailsError as any).response?.data.message;
        }
        else {
            return '';
        }
    } 

    return (
        <>
            <Spin spinning={isSetAutoRunDetailsLoading}>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title
                            style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}}
                            level={4}
                        >
                            Automatic Delivery Options
                        </Title>
                    </Text>
                </Space>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '95%' }}>
                {
                    (
                        (isSetAutoRunDetailsError)
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type="error"
                        ></ServaAlert>
                    )
                }
                {
                    <Form
                        form={formAutomaticDelivery}
                        initialValues = {{
                            adDescription: (report?.autoRun.description ?? ''),
                            adNextRun: formatDate(((report) && (report.id > 0)) ? report.autoRun.nextRun : new Date().toString(), dateFormat),
                            adDeliveryFormat: (report?.autoRun.deliveryFormat ?? 0).toString(),
                            adFileName: (report?.autoRun.fileName ?? ('Report_' + ((report) && (report.id > 0)) ? report?.id + '_File' : '0000_File')),
                            adRunFrequencyType: (report?.autoRun.frequencyType ?? 2).toString(),
                            adRunFrequencyValue: (report?.autoRun.frequencyValue ?? 1).toString(),
                            adEmailSubject: (report?.autoRun.subject ?? ''),
                            adEmailBody: (report?.autoRun.body ?? ''),
                            adParentAutoRunID: (report?.autoRun.parentAutoRunID ?? 0),
                            adReportInputID: (report?.autoRun.reportInputID ?? 0),
                            adPreRunSQL: (report?.autoRun.preSQL ?? ''),
                            adMonitorQueueQuery: (report?.autoRun.monitorQueueQuery ?? ''),
                        }}
                        key='automaticDelivery'
                        onFinish={(values: any) => onFinish(values)}
                        onFinishFailed={(error) => console.log(error)}
                    >
                        <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                            <Text italic type='secondary'>
                                {'[' + report?.autoRun.id + '] ' + report?.groupName + ': ' + report?.name + ' (' + report?.id + ')'}
                            </Text>
                        </Space>
                        <Divider style={{padding: 0, margin: 8}}></Divider>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <div style={{textAlign:'left'}}>
                                    <label>Description:</label>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='adDescription'
                                        rules={[
                                            {
                                                message: 'Description required.',
                                                required: true,
                                                validator: validateARDetails,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Input
                                            placeholder="Enter Auto Run Description"
                                            value={adDescription}
                                            onChange={() => handleADDescriptionUpdate()}
                                        ></Input>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={11}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='adNextRun'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <DateTimePicker
                                            label="Start Date and Time:"
                                            value={adNextRun}
                                            onChange={(e: string) => handleADNextRunUpdate(e)}
                                        />
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={2}/>
                            <Col span={11}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='adDeliveryFormat'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            label='Delivery Format:'
                                            value={adDeliveryFormat}
                                            onChange={(e: any) => handleADDeliveryFormatUpdate(e)}
                                        >
                                            <Option value="0">Excel</Option>
                                            <Option value="1">PDF</Option>
                                            <Option value="2">CSV</Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={2}/>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <div style={{textAlign:'left'}}>
                                    <label>File Name:</label>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='adFileName'
                                        rules={[
                                            {
                                                message: 'File Name required.',
                                                required: true,
                                                validator: validateARDetails,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Input
                                            placeholder="Enter Auto Run Report File Name"
                                            value={adFileName}
                                            onChange={() => handleADFileNameUpdate()}
                                        ></Input>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                            <Col span={11}>
                                <div style={{textAlign:'left'}}>
                                    <label>Run Frequency:</label>
                                </div>
                            </Col>
                            <Col span={2}/>
                            <Col span={11}>
                                <div style={{textAlign:'left'}}>
                                    <label>Run Frequency Value:</label>
                                </div>
                            </Col>
                            <Col span={2}/>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={11}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='adRunFrequencyType'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            value={adRunFrequencyType}
                                            onChange={(e: any) => handleADRunFrequencyTypeUpdate(e)}
                                        >
                                            <Option value="0">OnDemand</Option>
                                            <Option value="1">Daily</Option>
                                            <Option value="2">Weekly</Option>
                                            <Option value="3">Monthly</Option>
                                            <Option value="4">Yearly</Option>
                                            <Option value="5">EndOfWeek</Option>
                                            <Option value="6">EndOfMonth</Option>
                                            <Option value="7">EndOfYear</Option>
                                            <Option value="8">Weekday</Option>
                                            <Option value="9">Hourly</Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={2}/>
                            <Col span={11}>
                                <div style={{textAlign:'left', width:'50%'}}>
                                    <Form.Item
                                        name='adRunFrequencyValue'
                                        rules={[
                                            {
                                                message: 'Frequency Value required.',
                                                required: true,
                                                validator: validateARDetails,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Input
                                            size='middle'
                                            value={adRunFrequencyValue}
                                            onChange={() => handleADRunFrequencyValueUpdate()}
                                        ></Input>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={2}/>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <div style={{textAlign:'left'}}>
                                    <label>Email Subject:</label>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='adEmailSubject'
                                        rules={[
                                            {
                                                message: 'Email Subject required.',
                                                required: true,
                                                validator: validateARDetails,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Input
                                            placeholder="Enter Auto Run Email Subject"
                                            value={adEmailSubject}
                                            onChange={() => handleADEmailSubjectUpdate()}
                                        ></Input>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <div style={{textAlign:'left'}}>
                                    <label>Email Body:</label>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '8px', lineHeight: '50%' }}>
                            <Col span={24}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='adEmailBody'
                                        rules={[
                                            {
                                                message: 'Email Body required.',
                                                required: true,
                                                validator: validateARDetails,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <TextArea
                                            placeholder="Enter Auto Run Email Body"
                                            rows={3}
                                            style={{resize: 'none'}}
                                            value={adEmailBody}
                                            onChange={() => handleADEmailBodyUpdate()}
                                        ></TextArea>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        {
                            user?.runningUser.isSecurityAdmin && (
                                <>
                                    <Collapse
                                        style={{textAlign: 'left'}}
                                        items={[{
                                            key: '1',
                                            label: <><span>Advanced Options...</span><span><Text style={{fontStyle: 'italic'}} type='secondary'>&nbsp;(contact Application Support)</Text></span></>,
                                            children: <AutomaticDeliveryAdvanced
                                                        report={report}
                                                        adParentAutoRunID={adParentAutoRunID}
                                                        adReportInputID={adReportInputID}
                                                        adReportInputs={adReportInputs}
                                                        adPreRunSQL={adPreRunSQL}
                                                        adMonitorQueueQuery={adMonitorQueueQuery}
                                                        adFTPTarget= {adAutoRunTargetFTP}
                                                        adEmailTarget={adAutoRunTargetEmail}                                              
                                                        handleADAdvancedParentAutoRunIDUpdate={handleADParentAutoRunIDUpdate}
                                                        handleADAdvancedReportInputIDUpdate={handleADReportInputIDUpdate}
                                                        handleADAdvancedReportInputsUpdate={handleADReportInputsUpdate}
                                                        handleADAdvancedPreRunSQLUpdate={handleADPreRunSQLUpdate}
                                                        handleADAdvancedMonitorQueueQueryUpdate={handleADMonitorQueueQueryUpdate}
                                                        handleADAutoRunTargetEmailUpdate={handleADAutoRunTargetEmailUpdate}
                                                        handleADAutoRunTargetFTPUpdate={handleADAutoRunTargetFTPUpdate}
                                                    />,
                                        }]}
                                        size='small'
                                    />
                                </>
                            )
                        }
                        <Row style={{ padding: '8px', paddingTop: '8px', paddingBottom: '2px', lineHeight: '50%' }}>
                            <Col span={16}></Col>
                            <Col span={3} style={{display: 'flex', justifyContent: 'center'}}>
                                <div style={{ textAlign: 'right' }}>
                                    <Form.Item
                                        name="adApplyButton"
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Button
                                            //disabled={!adNextRun || adNextRun === ''}
                                            htmlType="submit"
                                            type="primary"
                                        >
                                            Apply
                                        </Button>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={2}></Col>
                            <Col span={3} style={{display: 'flex', justifyContent: 'center'}}>
                                <div style={{ textAlign: 'right' }}>
                                    <Form.Item
                                        name="adCancelButton"
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Button type="default" onClick={() => close()}>
                                            Cancel
                                        </Button>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                }
                </div>
            </Spin>
        </>
    );
};

export default AutomaticDelivery;
